import { useEffect, useRef } from "react";
import { GameEngine, type GameResult, type GameStats } from "../game/GameEngine";
import { ArenaClient, type ArenaStatus } from "../game/ArenaClient";
import { t } from "../i18n";

interface GameCanvasProps {
  mode: "solo" | "arena";
  skinColor: string;
  skinId: string;
  onStats: (stats: GameStats) => void;
  onFinish: (result: GameResult, arena?: { rank: number; coins: number }) => void;
  onArenaStatus?: (status: ArenaStatus, message?: string) => void;
}

export const SOLO_SECONDS = 120;
export const SOLO_BOTS = 7;

export function GameCanvas({ mode, skinColor, skinId, onStats, onFinish, onArenaStatus }: GameCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let arena: ArenaClient | undefined;
    const engine = new GameEngine(canvas, {
      duration: mode === "arena" ? 120 : SOLO_SECONDS,
      bots: mode === "solo" ? SOLO_BOTS : 0,
      playerName: t("game.you"),
      feedText: { kill: t("feed.kill"), killedBy: t("feed.killedBy"), you: t("feed.other") },
      skinColor,
      skinId,
      onStats,
      authoritative: mode === "arena",
      onTarget: (x, z) => arena?.sendTarget(x, z),
      onFinish: (result) => {
        if (mode === "solo") onFinish(result);
      },
    });
    void engine.start();

    if (mode === "arena") {
      arena = new ArenaClient({
        onStatus: onArenaStatus,
        onSnapshot: (snapshot) => engine.syncArenaSnapshot(arena?.samplePlayers() ?? snapshot.players, snapshot.items, arena?.playerId ?? 0),
        onEat: (playerId, itemId) => engine.onArenaEat(playerId, itemId),
        onResult: (rank, score, coins) => onFinish({ score, eaten: engine.eatenCount }, { rank, coins }),
      });
      arena.connect();
    }

    return () => {
      arena?.close();
      engine.destroy();
    };
  }, [mode, onArenaStatus, onFinish, onStats, skinColor, skinId]);

  return <canvas ref={canvasRef} className="game-canvas" aria-label="Dunkles Loch 3D-Spielfeld" />;
}
